import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Badge,
  Stack,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import type { GridColDef, GridRowsProp } from "@mui/x-data-grid-pro";

import SearchAndFilterArea from "../components/SearchAndFilterArea";
import SendEmailDrawer from "../components/SendEmailDrawer";
import ActionsMenu from "../components/ActionsMenu";
import EmptyState from "../components/EmptyState";
import DownloadIndicator from "../components/DownloadIndicator";
import SearchResults from "../components/SearchResults";
import PolicyGridView from "../components/PolicyGridView";
import DocumentView from "../components/DocumentView";
import DocumentTypesList from "../components/DocumentTypesList";
import { useDocumentManagement } from "../hooks/useDocumentManagement";
import { useErrorDialogSetup } from "../hooks/useErrorDialogSetup";
import type { AdvancedSearchParams } from "../types/search";
import { policyApiService } from "../services/policyApiService";
import { downloadPolicyDocumentComplete } from "../services/policyDownloadService";
import type { PolicyDownloadRequest } from "../services/policyDownloadService";
import type { PolicyDocument, UserContext } from "../types/api";
import { useOidcAuth } from "../contexts/OidcAuthContext";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Type for table row data (converted from PolicyDocument)
interface PolicyTableRowData {
  id: number;
  policyCode: string;
  policyName: string;
  uploadDate: Date | null;
  createdDate: Date | null;
  updateDate: Date | null;
  description: string;
  documentType: string;
  category: string;
  subcategory: string;
  agencyName: string;
  agencyId: number;
  platform_org_id: string;
  audienceName: string;
  versionNumber: string;
  language: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-grid-tabpanel-${index}`}
      aria-labelledby={`data-grid-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Convert PolicyDocument to GridRowsProp format
const convertToGridRows = (policies: PolicyDocument[]): GridRowsProp => {
  return policies.map((policy: PolicyDocument) => {
    // Handle date fields with proper fallbacks
    const publishedDate =
      policy.published_date && policy.published_date.trim() !== ""
        ? new Date(policy.published_date)
        : null;

    const createdDate =
      policy.created_date && policy.created_date.trim() !== ""
        ? new Date(policy.created_date)
        : null;

    const updatedDate =
      policy.updated_date && policy.updated_date.trim() !== ""
        ? new Date(policy.updated_date)
        : null;

    return {
      id: policy.id,
      policyCode: policy.policy_code,
      policyName: policy.name,
      uploadDate: publishedDate ? publishedDate : createdDate, // Upload Date = Published Date if available, otherwise Created Date
      createdDate: createdDate, // Recently Added = Created Date
      updateDate: updatedDate, // Recently Updated = Updated Date
      description: policy.description,
      documentType: policy.document_type,
      category: policy.category,
      subcategory: policy.subcategory,
      agencyName: policy.agency.name,
      agencyId: policy.agency.id,
      platform_org_id: policy.agency.platform_org_id,
      audienceName: policy.audience.name,
      versionNumber: policy.version_number,
      language: policy.language,
    };
  });
};

// Scrollable container selector constant for infinite scroll (Atlas GN App)
const SCROLLABLE_CONTAINER_SELECTOR = "[data-atlas-gn-app]";

const DataGridPage: React.FC = () => {
  // Set up global error dialog handler for download services
  useErrorDialogSetup();

  // Get OIDC auth loading state to prevent race conditions
  const { isLoading: oidcLoading } = useOidcAuth();

  const {
    documentTypes,
    documentTypesLoading,
    documentTypesError,
    documentsByType,
    documentsByTypeLoading,
    documentsByTypeError,
    selectedFolder,
    loadDocumentTypes,
    handleFolderClick,
    handleBackToFolders,
  } = useDocumentManagement();

  const [tabValue, setTabValue] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSortOption, setCurrentSortOption] =
    useState<string>("all-documents");
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [currentAdvancedSearchParams, setCurrentAdvancedSearchParams] =
    useState<AdvancedSearchParams | null>(null);

  // API state
  const [policies, setPolicies] = useState<PolicyDocument[]>([]);
  const [, setUserContext] = useState<UserContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // Pagination state for table view
  const [allPoliciesPaginationModel, setAllPoliciesPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // OPTIMIZED: Infinite scroll state for grid view
  const [gridViewPage, setGridViewPage] = useState(1);
  const [hasMorePolicies, setHasMorePolicies] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Use ref to prevent race condition in concurrent load calls
  const isLoadingRef = useRef(false);

  // AbortController to cancel in-flight requests when switching views
  const abortControllerRef = useRef<AbortController | null>(null);

  // Send Email Drawer State
  const [sendEmailDrawerOpen, setSendEmailDrawerOpen] = useState(false);
  const [selectedPolicyForEmail, setSelectedPolicyForEmail] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Mobile filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // View mode state
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));

  // Responsive font size functions
  const getMainTitleFontSize = () => {
    if (isXs) return "1.5rem";
    if (isSm) return "1.625rem";
    if (isMd) return "1.75rem";
    return "1.875rem";
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);

    // Load document types when switching TO Index tab (lazy loading)
    if (newValue === 1 && documentTypes.length === 0 && !documentTypesLoading) {
      loadDocumentTypes();
    }

    // Reset folder selection when switching tabs
    if (newValue !== 1) {
      handleBackToFolders();
    }
    // Clear search state when switching away from search
    if (isSearchActive && newValue !== 0) {
      setIsSearchActive(false);
      setSearchQuery("");
      setCurrentAdvancedSearchParams(null); // Clear advanced search params
      setSearchValue("");
    }
  };

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement> | string,
  ) => {
    const value = typeof event === "string" ? event : event.target.value;
    setSearchValue(value);
  };

  const handleSearchKeyPress = (
    event: React.KeyboardEvent,
    searchTerm: string,
  ) => {
    if (event.key === "Enter" && searchTerm.trim()) {
      // Clear advanced search params for regular search
      setCurrentAdvancedSearchParams(null);

      setSearchQuery(searchTerm);
      setIsSearchActive(true);
      setTabValue(0); // Switch to All Policies tab and show SearchResults component

      // Set search value to keep UI consistent
      setSearchValue(searchTerm);
    }
  };

  // Suggestions disabled

  const handleBackToAllPolicies = () => {
    setIsSearchActive(false);
    setSearchQuery("");
    setSearchValue("");
    setCurrentAdvancedSearchParams(null); // Clear advanced search params
  };

  const handleOpenSendEmailDrawer = (policyId: number, policyName: string) => {
    setSelectedPolicyForEmail({ id: policyId, name: policyName });
    setSendEmailDrawerOpen(true);
  };

  const handleCloseSendEmailDrawer = () => {
    setSendEmailDrawerOpen(false);
    setSelectedPolicyForEmail(null);
  };

  const handleGridViewToggle = () => {
    setViewMode(viewMode === "table" ? "grid" : "table");
  };

  const handleSortSelection = (sortOption: string) => {
    setCurrentSortOption(sortOption);
  };

  const handleLanguageSelection = (languageOption: string) => {
    setSelectedLanguage(languageOption === "all" ? null : languageOption);
    setAllPoliciesPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Helper function to create download request from PolicyDocument (grid view)
  const createDownloadRequestFromPolicy = (
    policy: PolicyDocument,
  ): PolicyDownloadRequest => ({
    agencyId: policy.agency.id.toString(),
    platform_org_id: policy.agency.platform_org_id,
    policyDocument: {
      id: policy.id.toString(),
      name: policy.name,
    },
    latestVersion: {
      versionNumber: policy.version_number || "1.0",
    },
    languageCode: policy.language || "EN",
  });

  // Helper function to create download request from row data (table view)
  const createDownloadRequestFromRowData = (
    rowData: PolicyTableRowData,
  ): PolicyDownloadRequest => ({
    agencyId: rowData.agencyId?.toString(),
    platform_org_id: rowData.platform_org_id,
    policyDocument: {
      id: rowData.id.toString(),
      name: rowData.policyName,
    },
    latestVersion: {
      versionNumber: rowData.versionNumber || "1.0",
    },
    languageCode: rowData.language || "EN",
  });

  // Main download handler that works with both PolicyDocument and table row data
  const handlePolicyDownload = async (
    policyOrRowData: PolicyDocument | PolicyTableRowData,
  ) => {
    try {
      const isTableRowData = "policyName" in policyOrRowData;
      const key = isTableRowData
        ? `${(policyOrRowData as PolicyTableRowData).id}-${
            (policyOrRowData as PolicyTableRowData).language || ""
          }`
        : `${(policyOrRowData as PolicyDocument).id}-${
            (policyOrRowData as PolicyDocument).language || ""
          }`;

      if (downloadingIds.has(key)) return;
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      const downloadRequest = isTableRowData
        ? createDownloadRequestFromRowData(
            policyOrRowData as PolicyTableRowData,
          )
        : createDownloadRequestFromPolicy(policyOrRowData as PolicyDocument);

      await downloadPolicyDocumentComplete(downloadRequest);
    } catch (error) {
      console.error("Download failed:", error);
      // You might want to show a toast notification here
    } finally {
      const isTableRowData = "policyName" in policyOrRowData;
      const key = isTableRowData
        ? `${(policyOrRowData as PolicyTableRowData).id}-${
            (policyOrRowData as PolicyTableRowData).language || ""
          }`
        : `${(policyOrRowData as PolicyDocument).id}-${
            (policyOrRowData as PolicyDocument).language || ""
          }`;
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Load policies data
  // OPTIMIZED: Load initial policies for grid view (50 items)
  const loadPolicies = useCallback(async () => {
    // Cancel any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setLoading(true);
      setError(null);

      if (viewMode === "grid") {
        // OPTIMIZED: For grid view, load only first 50 policies for fast initial render
        const response = await policyApiService.getPolicies({
          page: 1,
          limit: 50,
          language: selectedLanguage || undefined,
          signal: abortController.signal, // Pass abort signal
        });

        // Check if request was aborted before updating state
        if (abortController.signal.aborted) return;

        setPolicies(response.data);
        setUserContext(response.user_context);
        setTotalCount(response.pagination.total);

        // Reset grid view pagination state
        setGridViewPage(1);
        setHasMorePolicies(
          response.pagination.page < response.pagination.totalPages,
        );
      } else {
        // For table view, use pagination as before
        const response = await policyApiService.getPolicies({
          page: allPoliciesPaginationModel.page + 1, // API uses 1-based pagination
          limit: allPoliciesPaginationModel.pageSize,
          language: selectedLanguage || undefined,
          signal: abortController.signal, // Pass abort signal
        });

        // Check if request was aborted before updating state
        if (abortController.signal.aborted) return;

        setPolicies(response.data);
        setUserContext(response.user_context);
        setTotalCount(response.pagination.total);
      }
    } catch (err) {
      // Don't show error if request was aborted (user switched views)
      if (err instanceof Error && err.name === "AbortError") {
        console.log("Request cancelled:", err.message);
        return;
      }

      // Only show error if this is the current request
      if (!abortController.signal.aborted) {
        setError(
          err instanceof Error ? err.message : "Failed to load policies",
        );
        console.error("Error loading policies:", err);
      }
    } finally {
      // Only clear loading if this is still the current request
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [viewMode, allPoliciesPaginationModel, selectedLanguage]);

  // OPTIMIZED: Load more policies for infinite scroll
  const loadMorePolicies = useCallback(async () => {
    // Check ref first for synchronous guard against race conditions
    if (
      isLoadingRef.current ||
      loadingMore ||
      !hasMorePolicies ||
      viewMode !== "grid"
    )
      return;

    try {
      // Set ref immediately (synchronous) to prevent concurrent calls
      isLoadingRef.current = true;
      setLoadingMore(true);
      const nextPage = gridViewPage + 1;

      // Pass abort signal to cancel infinite scroll requests when switching views
      const response = await policyApiService.getPolicies({
        page: nextPage,
        limit: 50,
        language: selectedLanguage || undefined,
        signal: abortControllerRef.current?.signal, // Pass abort signal
      });

      // Check if request was aborted before updating state
      if (abortControllerRef.current?.signal.aborted) return;

      // Append new policies to existing ones and calculate if more are available
      setPolicies((prevPolicies) => {
        const newPolicies = [...prevPolicies, ...response.data];

        // Check multiple conditions to determine if there are more policies
        const hasMore =
          response.pagination.page < response.pagination.totalPages &&
          response.data.length > 0 &&
          newPolicies.length < response.pagination.total;

        setHasMorePolicies(hasMore);
        return newPolicies;
      });
      setGridViewPage(nextPage);
    } catch (err) {
      // Don't show error if request was aborted (user switched views)
      if (err instanceof Error && err.name === "AbortError") {
        console.log("Infinite scroll request cancelled:", err.message);
        return;
      }

      console.error("Error loading more policies:", err);
      // Stop trying to load more on error
      setHasMorePolicies(false);
    } finally {
      // Reset ref in finally to allow future calls
      isLoadingRef.current = false;
      setLoadingMore(false);
    }
  }, [loadingMore, hasMorePolicies, gridViewPage, viewMode, selectedLanguage]);

  // OPTIMIZED: Detect when user scrolls near bottom for infinite scroll
  useEffect(() => {
    if (viewMode !== "grid" || !hasMorePolicies) return;

    const handleScroll = () => {
      // Double-check conditions (including ref) before triggering load
      if (
        isLoadingRef.current ||
        !hasMorePolicies ||
        loadingMore ||
        viewMode !== "grid"
      )
        return;

      const scrollableElement = document.querySelector(
        SCROLLABLE_CONTAINER_SELECTOR,
      );
      if (!scrollableElement) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollableElement;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Load more when user scrolls to 80% of the page
      if (scrollPercentage > 0.8) {
        loadMorePolicies();
      }
    };

    const scrollableElement = document.querySelector(
      SCROLLABLE_CONTAINER_SELECTOR,
    );
    scrollableElement?.addEventListener("scroll", handleScroll);

    return () => {
      scrollableElement?.removeEventListener("scroll", handleScroll);
    };
  }, [viewMode, hasMorePolicies, loadingMore, loadMorePolicies]);

  // Cleanup - abort any in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reset state when switching view modes or applying filters (MUST run before load effects)
  useEffect(() => {
    setPolicies([]);
    // OPTIMIZED: Reset infinite scroll state
    setGridViewPage(1);
    setHasMorePolicies(true);
    // Reset loading ref when resetting state
    isLoadingRef.current = false;
    // Note: Previous request will be aborted by loadPolicies via AbortController
  }, [viewMode, selectedLanguage]);

  // Load policies data - separate effects for table vs grid view
  // Table view: reload when pagination or language changes
  useEffect(() => {
    // Wait for OIDC loading to complete before making API calls
    if (oidcLoading) return;

    if (viewMode === "table") {
      loadPolicies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allPoliciesPaginationModel.page,
    allPoliciesPaginationModel.pageSize,
    selectedLanguage,
    viewMode,
    oidcLoading,
  ]);

  // Grid view: reload only when switching to grid view or language changes
  useEffect(() => {
    // Wait for OIDC loading to complete before making API calls
    if (oidcLoading) return;

    if (viewMode === "grid") {
      loadPolicies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedLanguage, oidcLoading]);

  // Apply frontend sorting since backend doesn't support these sort options
  const applySorting = (data: GridRowsProp): GridRowsProp => {
    if (currentSortOption === "all-documents") {
      return data;
    }

    const sortedData = [...data];

    switch (currentSortOption) {
      case "recently-updated":
        return sortedData.sort((a: any, b: any) => {
          const dateA = a.updateDate ? new Date(a.updateDate).getTime() : 0;
          const dateB = b.updateDate ? new Date(b.updateDate).getTime() : 0;
          return dateB - dateA;
        });

      case "recently-added":
        return sortedData.sort((a: any, b: any) => {
          const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          return dateB - dateA;
        });

      default:
        return sortedData;
    }
  };

  // Apply frontend sorting to PolicyDocument array for grid view
  const applySortingToPolicies = (data: PolicyDocument[]): PolicyDocument[] => {
    if (currentSortOption === "all-documents") {
      return data;
    }

    const sortedData = [...data];

    switch (currentSortOption) {
      case "recently-updated":
        return sortedData.sort((a, b) => {
          // Validate and parse updated_date with trim check (like convertToGridRows)
          const dateA =
            a.updated_date && a.updated_date.trim() !== ""
              ? new Date(a.updated_date).getTime()
              : 0;
          const dateB =
            b.updated_date && b.updated_date.trim() !== ""
              ? new Date(b.updated_date).getTime()
              : 0;
          // Guard against NaN from invalid date strings
          const timeA = Number.isFinite(dateA) ? dateA : 0;
          const timeB = Number.isFinite(dateB) ? dateB : 0;
          return timeB - timeA;
        });

      case "recently-added":
        return sortedData.sort((a, b) => {
          // Validate and parse created_date with trim check (like convertToGridRows)
          const dateA =
            a.created_date && a.created_date.trim() !== ""
              ? new Date(a.created_date).getTime()
              : 0;
          const dateB =
            b.created_date && b.created_date.trim() !== ""
              ? new Date(b.created_date).getTime()
              : 0;
          // Guard against NaN from invalid date strings
          const timeA = Number.isFinite(dateA) ? dateA : 0;
          const timeB = Number.isFinite(dateB) ? dateB : 0;
          return timeB - timeA;
        });

      default:
        return sortedData;
    }
  };

  // Memoize sorted policies to avoid re-sorting on every render
  const sortedPolicies = useMemo(() => {
    return applySortingToPolicies(policies);
  }, [policies, currentSortOption]);

  const getGridData = (): GridRowsProp => {
    // Search results are handled by SearchResults component, not table
    return applySorting(convertToGridRows(policies));
  };

  const allPoliciesColumns: GridColDef[] = [
    {
      field: "policyName",
      headerName: "Policy Name",
      flex: 1,
      type: "string",
      renderCell: (params) => {
        const key = `${params.row.id}-${params.row.language || ""}`;
        const isDownloading = downloadingIds.has(key);
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ height: "100%" }}
          >
            <Typography
              onClick={() => handlePolicyDownload(params.row)}
              sx={{
                textDecoration: "underline",
                cursor: isDownloading ? "default" : "pointer",
                pointerEvents: isDownloading ? "none" : "auto",
                opacity: isDownloading ? 0.6 : 1,
                color: "text.primary",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              {params.value}
            </Typography>
            {isDownloading && <DownloadIndicator size={16} />}
          </Stack>
        );
      },
    },
    {
      field: "uploadDate",
      headerName: "Upload Date",
      flex: 1,
      type: "date",
      renderCell: (params) => (
        <Typography
          sx={{
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          {params.value
            ? new Date(params.value)
                .toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
                .replace(/\//g, "/")
            : "-"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu
          rowId={params.row.id}
          policyName={params.row.policyName}
          versionNumber={params.row.versionNumber || "1.0"}
          agencyId={params.row.agencyId?.toString()}
          platform_org_id={params.row.platform_org_id}
          language={params.row.language || "EN"}
          onSendEmail={handleOpenSendEmailDrawer}
        />
      ),
    },
  ];

  return (
    <Box>
      <Typography
        component="h1"
        gutterBottom
        sx={{
          color: "text.primary",
          fontSize: getMainTitleFontSize(),
          fontWeight: 600,
          lineHeight: isXs
            ? "1.875rem"
            : isSm
              ? "2rem"
              : isMd
                ? "2.125rem"
                : "2.375rem",
          wordWrap: "break-word",
          mb: 2,
        }}
      >
        Policy Portal
      </Typography>

      <Paper elevation={0} sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="data grid tabs"
          >
            <Tab
              label={
                <Badge
                  badgeContent={totalCount}
                  color="primary"
                  showZero={false}
                >
                  <Box sx={{ mr: 1 }}>All Policies</Box>
                </Badge>
              }
              id="data-grid-tab-0"
            />
            <Tab label="Index" id="data-grid-tab-1" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ width: "100%", pt: 3, pb: 1 }}>
            <SearchAndFilterArea
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              onSearchKeyPress={(event) =>
                handleSearchKeyPress(event, searchValue)
              }
              searchPlaceholder="Search all policies..."
              showSearch={true}
              showGridView={!isSearchActive}
              showSortBy={!isSearchActive}
              showLanguage={!isSearchActive}
              showFilterButton={isSearchActive && (isXs || isSm || isMd)}
              onOpenFilter={() => setIsFilterDrawerOpen(true)}
              viewMode={viewMode}
              onGridView={handleGridViewToggle}
              onSortSelection={handleSortSelection}
              onLanguageSelection={handleLanguageSelection}
            />

            {isSearchActive ? (
              <SearchResults
                searchQuery={searchQuery}
                currentSearchText={searchValue}
                onBack={handleBackToAllPolicies}
                advancedSearchParams={currentAdvancedSearchParams || undefined}
                filterDrawerOpen={isFilterDrawerOpen}
                onCloseFilterDrawer={() => setIsFilterDrawerOpen(false)}
              />
            ) : loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="400px"
              >
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : policies.length === 0 ? (
              <EmptyState
                title="No items to display."
                subtitle="No policies yet"
              />
            ) : viewMode === "table" ? (
              <DataGridPro
                disableColumnMenu
                disableColumnSorting
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                rows={getGridData()}
                columns={allPoliciesColumns}
                pagination
                paginationMode="server"
                density="compact"
                pageSizeOptions={[10, 25, 50]}
                paginationModel={allPoliciesPaginationModel}
                onPaginationModelChange={(model) =>
                  setAllPoliciesPaginationModel((prev) => {
                    const sizeChanged = model.pageSize !== prev.pageSize;
                    if (sizeChanged) {
                      // Clear policies to avoid transient mixed pages
                      setPolicies([]);
                    }
                    return {
                      page: sizeChanged ? 0 : model.page,
                      pageSize: model.pageSize,
                    };
                  })
                }
                disableRowSelectionOnClick
                rowCount={totalCount}
                getRowId={(row) => `${row.id}-${row.language || ""}`}
                localeText={{
                  MuiTablePagination: {
                    labelRowsPerPage: "Rows",
                  },
                }}
                sx={{
                  width: "100%",
                  height: "calc(100vh - 300px)",
                  "& .MuiDataGrid-footerContainer": {
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "background.paper",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    zIndex: 1,
                  },
                  "& .MuiDataGrid-toolbarContainer": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    boxShadow: "none",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    boxShadow: "none",
                  },
                }}
              />
            ) : (
              <>
                <PolicyGridView
                  policies={sortedPolicies}
                  loading={loading}
                  onSendEmail={handleOpenSendEmailDrawer}
                  onPolicyDownload={handlePolicyDownload}
                  agencyId={
                    policies.length > 0 ? policies[0].agency.id.toString() : ""
                  }
                />
                {/* OPTIMIZED: Infinite scroll loading indicator */}
                {loadingMore && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={3}
                  >
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" ml={2}>
                      Loading more policies...
                    </Typography>
                  </Box>
                )}
                {/* Show total count and loaded count */}
                {!loading && policies.length > 0 && viewMode === "grid" && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {policies.length} of {totalCount} policies
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ width: "100%", pt: 3, pb: 1 }}>
            {documentTypes.length === 0 && documentTypesLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="400px"
              >
                <CircularProgress />
              </Box>
            ) : documentTypes.length === 0 && documentTypesError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {documentTypesError}
              </Alert>
            ) : selectedFolder ? (
              <Box>
                <SearchAndFilterArea
                  searchValue={searchValue}
                  onSearchChange={handleSearchChange}
                  onSearchKeyPress={(event) =>
                    handleSearchKeyPress(event, searchValue)
                  }
                  searchPlaceholder="Search all policies..."
                  showSearch={true}
                  showGridView={false}
                  showSortBy={false}
                  showLanguage={false}
                />

                <DocumentView
                  selectedFolder={selectedFolder}
                  documentsByType={documentsByType}
                  documentsByTypeLoading={documentsByTypeLoading}
                  documentsByTypeError={documentsByTypeError}
                  onBackToFolders={handleBackToFolders}
                />
              </Box>
            ) : (
              <Box>
                <SearchAndFilterArea
                  searchValue={searchValue}
                  onSearchChange={handleSearchChange}
                  onSearchKeyPress={(event) =>
                    handleSearchKeyPress(event, searchValue)
                  }
                  searchPlaceholder="Search all policies..."
                  showSearch={true}
                  showGridView={false}
                  showSortBy={false}
                  showLanguage={false}
                />

                <DocumentTypesList
                  documentTypes={documentTypes}
                  documentTypesLoading={documentTypesLoading}
                  documentTypesError={documentTypesError}
                  onFolderClick={handleFolderClick}
                />
              </Box>
            )}
          </Box>
        </TabPanel>
      </Paper>

      <SendEmailDrawer
        open={sendEmailDrawerOpen}
        onClose={handleCloseSendEmailDrawer}
        policyId={selectedPolicyForEmail?.id || 0}
        policyName={selectedPolicyForEmail?.name || ""}
      />
    </Box>
  );
};

export default DataGridPage;
